// Procedimientos de SST para trabajos especiales (lote D, M49, Anexo 1 GT 37): uno por cada uno de los 9 tipos de
// permiso de trabajo. El borrador se genera con lo que YA define cada tipo en StorSafe: su nombre, las preguntas del
// checklist del permiso (controles y EPP que se verifican) y los peligros de la Matriz de Riesgos de la empresa que
// corresponden a ese tipo (el mismo mapa que usa Permisos de Trabajo para sugerir el tipo). Todo queda editable.
// La versión aprobada se congela y se firma como los informes por puesto; la socialización la firman los trabajadores
// (registro de M45 con el tipo socializacion).

import { HAZARD_TO_PERMIT_TYPE } from '../../constants/hazardPermitMap';
import { canonico } from '../firmas/firmas-logica';
import { sha256Hex } from '../psicosocial-codigos';

export const TIPOS_PERMISO = ['altura', 'caliente', 'confinado', 'electrico', 'quimico', 'loto', 'excavacion', 'maquinaria', 'vias'] as const;
export type TipoPermiso = typeof TIPOS_PERMISO[number];
/** Los mismos nombres de Permisos de Trabajo. */
export const ETIQUETA_TIPO_PERMISO: Record<TipoPermiso, string> = {
  altura: 'Trabajo en altura', caliente: 'Trabajo en caliente', confinado: 'Espacio confinado', electrico: 'Trabajo eléctrico',
  quimico: 'Químico / Sustancias Peligrosas', loto: 'Bloqueo y Etiquetado (LOTO)', excavacion: 'Excavación / Zanjas',
  maquinaria: 'Maquinaria Pesada', vias: 'Trabajo en Vías Públicas',
};

export interface FilaProcedimiento {
  id: string; company_id: string; permit_type: TipoPermiso; objective: string | null; scope: string | null; responsibles: string | null;
  position_ids: string[]; exposed_count: number | null; exposed_notes: string | null; activities: string | null; risks: string | null;
  controls: string | null; ppe: string | null; created_at: string; updated_at: string;
}
export type EditableProcedimiento = Pick<FilaProcedimiento, 'objective' | 'scope' | 'responsibles' | 'position_ids' | 'exposed_count'
  | 'exposed_notes' | 'activities' | 'risks' | 'controls' | 'ppe'>;

export interface PreguntaPermiso { item_text: string; display_order: number; referencia?: string | null }
export interface PeligroIper { hazard_type: string | null; hazard_description: string | null; possible_effects: string | null }

/** ¿La pregunta del checklist habla del EPP? (para separar el EPP de los demás controles) */
const ES_EPP = /\bEPP\b|protecci[oó]n (personal|ocular|respiratoria|auditiva|diel[eé]ctric)|arn[eé]s|casco|guantes|careta|gafas|respirador|calzado|ropa/i;
const sinSignos = (t: string) => t.trim().replace(/^¿/, '').replace(/\?$/, '');

/** Borrador generado desde lo que ya define el tipo de permiso. */
export function borradorProcedimiento(tipo: TipoPermiso, preguntas: PreguntaPermiso[], peligros: PeligroIper[]): EditableProcedimiento {
  const nombre = ETIQUETA_TIPO_PERMISO[tipo];
  const ord = [...preguntas].sort((a, b) => a.display_order - b.display_order);
  const epp = ord.filter(p => ES_EPP.test(p.item_text));
  const controles = ord.filter(p => !ES_EPP.test(p.item_text));
  const hazards = Object.entries(HAZARD_TO_PERMIT_TYPE).filter(([, t]) => t === tipo).map(([h]) => h);
  const deEmpresa = peligros.filter(p => p.hazard_type && hazards.includes(p.hazard_type));
  const riesgos = Array.from(new Set([
    ...hazards.map(h => `Peligro: ${h}.`),
    ...deEmpresa.map(p => [p.hazard_description, p.possible_effects].filter(Boolean).join(': ')).filter(Boolean),
  ]));
  return {
    objective: `Establecer cómo se planifica, autoriza, ejecuta y cierra un ${nombre.toLowerCase()} para prevenir accidentes y daños a la salud de quienes lo realizan y de terceros.`,
    scope: `Todos los ${nombre.toLowerCase()} que realicen trabajadores propios o contratistas en las instalaciones de la empresa.`,
    responsibles: 'Quien autoriza el permiso: verifica las condiciones y firma la autorización y el cierre.\nTrabajador ejecutor: cumple el procedimiento, usa el EPP y firma el permiso.\nResponsable de SSO: mantiene este procedimiento, lo socializa y verifica su cumplimiento.',
    position_ids: [], exposed_count: null, exposed_notes: '',
    activities: ['Solicitar y emitir el permiso de trabajo antes de empezar.', ...controles.map(p => `Verificar: ${sinSignos(p.item_text)}.`),
      'Firmar el permiso (quien autoriza y los trabajadores ejecutores) y mantenerlo en el lugar del trabajo.', 'Cerrar el permiso al terminar, con la firma de cierre.']
      .join('\n'),
    risks: riesgos.join('\n'),
    controls: controles.map(p => `${sinSignos(p.item_text)}${p.referencia ? ` (${p.referencia})` : ''}.`).join('\n'),
    ppe: epp.map(p => `${sinSignos(p.item_text)}.`).join('\n'),
  };
}

export interface ContenidoProcedimiento extends EditableProcedimiento {
  procedimiento_id: string; tipo_permiso: TipoPermiso; nombre: string; empresa: string; fecha_corte: string;
  puestos: string[]; formato_permiso: string[];
}
export function contenidoProcedimiento(p: FilaProcedimiento, empresa: string, puestos: { id: string; name: string }[], preguntas: PreguntaPermiso[], hoyIso: string): ContenidoProcedimiento {
  const t = (v: string | null) => (v ?? '').trim();
  return {
    procedimiento_id: p.id, tipo_permiso: p.permit_type, nombre: ETIQUETA_TIPO_PERMISO[p.permit_type], empresa, fecha_corte: hoyIso,
    objective: t(p.objective), scope: t(p.scope), responsibles: t(p.responsibles), position_ids: [...p.position_ids].sort(),
    puestos: p.position_ids.map(id => puestos.find(x => x.id === id)?.name ?? '').filter(Boolean).sort((a, b) => a.localeCompare(b, 'es')),
    exposed_count: p.exposed_count, exposed_notes: t(p.exposed_notes), activities: t(p.activities), risks: t(p.risks), controls: t(p.controls), ppe: t(p.ppe),
    formato_permiso: [...preguntas].sort((a, b) => a.display_order - b.display_order).map(q => q.item_text.trim()),
  };
}
export const hashProcedimiento = (c: ContenidoProcedimiento) => sha256Hex(canonico(c));

export function faltantesProcedimiento(c: ContenidoProcedimiento): string[] {
  const f: string[] = [];
  if (!c.objective) f.push('Falta el objetivo.');
  if (!c.responsibles) f.push('Faltan los responsables.');
  if (c.puestos.length === 0) f.push('Elige al menos un puesto de trabajo.');
  if (c.exposed_count === null) f.push('Indica el número de trabajadores expuestos.');
  if (!c.activities) f.push('Faltan las actividades.');
  if (!c.risks) f.push('Faltan los riesgos.');
  if (!c.controls) f.push('Faltan los controles.');
  if (!c.ppe) f.push('Falta el EPP.');
  return f;
}
